import { CdkDragMove, CdkDragRelease, CdkDragStart } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { LoggingService } from "@merri/common";
import { Subject, fromEvent, merge } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { LocalStorageKeys, SnackBarService, StorageService } from "src/app/core";
import { EventVenuesService } from "src/app/features/events";
import { HelperMethods } from "src/app/shared";
import { DragDropPanelType, Rect, VisionboardItem, VisionboardMode } from "../../models";
import {
  VisionboardDragDropService,
  VisionboardService,
  VisionboardVersionsService,
  VisionboardWorkspaceFreeformService,
  VisionboardWorkspaceItemsStoreService,
} from "../../services";

@Component({
    selector: "app-visionboard-workspace-freeform-venue-items",
    templateUrl: "./visionboard-workspace-freeform-venue-items.component.html",
    styleUrls: ["./visionboard-workspace-freeform-venue-items.component.scss"],
    standalone: false
})
export class VisionboardWorkspaceFreeformVenueItemsComponent implements OnInit, OnDestroy {
  private _fullItemSet: VisionboardItem[];
  private _destroyed$: Subject<void>;
  private _hasLoaded: boolean;

  items: VisionboardItem[];
  externalDrag: boolean;
  dragging: boolean;
  allowDrop: boolean;
  hide: boolean;
  header: string;
  hideItems: boolean;
  message: string;
  panelOpenState: boolean = false;
  animateOpenState: boolean = false;
  sizeFactor: number;

  constructor(
    private visionboardService: VisionboardService,
    private eventVenuesService: EventVenuesService,
    private visionboardDragDropService: VisionboardDragDropService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardWorkspaceFreeformService: VisionboardWorkspaceFreeformService,
    private snackBarService: SnackBarService,
    private visionboardVersionsService: VisionboardVersionsService,
    private _storageService: StorageService,
  ) {
    LoggingService.log("VisionboardWorkspaceVenueItemsComponent - ctor");
    this._destroyed$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: update => {
        if (!update.refresh && this._hasLoaded) {
          LoggingService.log("VisionboardWorkspaceVenueItemsComponent - items$ - skip - no refresh");
          return;
        }

        if (this.visionboardService.getCurrentMode() !== VisionboardMode.Freeform) {
          LoggingService.log("VisionboardWorkspaceVenueItemsComponent - items$ - skip - not freeform");
          return;
        }

        this._hasLoaded = true;
        this.hideItems = true;

        // avoid passing in the source to the tetris alg bc it is configured to remove overflows (need all items for Category)
        this.items = update.store.venueItems && [...update.store.venueItems];

        this._fullItemSet = this.items?.slice();

        this.sizeFactor = this.visionboardWorkspaceFreeformService.calculateSizeFactor(this._fullItemSet?.length);

        if (!this.items && update.store.failed) {
          this.message = "FAILED TO LOAD ITEMS, TRY AGAIN";
        } else if (this.items && this.items.length === 0) {
          this.message = "NO ITEMS ARE PROVIDED BY THIS VENUE";
        } else {
          this.message = null;
        }

        Promise.resolve(null).then(() => {
          this.setHeightAndRecalcItemLocations(this.sizeFactor);
          this.hideItems = false;
        });
      },
    });

    this.visionboardDragDropService.hover$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: data => {
        this.dragging = data && !!data.source;
        this.allowDrop = this.dragging && data.source === DragDropPanelType.WorkspaceVenueItems;
        this.externalDrag = this.dragging && data.source !== DragDropPanelType.WorkspaceVenueItems;
      },
    });

    this.visionboardVersionsService.selectedVersion$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: version => {
        this.setHeader();

        this.hide = !version;
      },
    });

    this.eventVenuesService.currentEventVenues$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: () => {
        this.setHeader();
      },
    });

    merge(fromEvent(window, "resize"), fromEvent(window, "resize").pipe(debounceTime(100)))
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this._resetItems();
          this.setHeightAndRecalcItemLocations(this.sizeFactor);
        },
      });

    if (this._storageService.local.get(LocalStorageKeys.VisionboardFreeformVenueItemsCollapsed) === null) {
      this._storageService.local.set(LocalStorageKeys.VisionboardFreeformVenueItemsCollapsed, "0");
    }

    this.panelOpenState = this._storageService.local.compare(LocalStorageKeys.VisionboardFreeformVenueItemsCollapsed, "0");
    this.animateOpenState = this.panelOpenState;
  }

  private setHeader(): void {
    const version = this.visionboardVersionsService.getSelectedVersion();

    if (version) {
      const venueName = this.eventVenuesService.getCurrentVenueNames();
      this.header = `ITEMS PROVIDED BY ${venueName[0] || "VENUE"}`;
    } else {
      this.header = "SELECT VERSION";
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();

    LoggingService.log("VisionboardWorkspaceVenueItemsComponent - destroy");
  }

  onDragStart(event: CdkDragStart, item: VisionboardItem): void {
    HelperMethods.moveItemToEndOfArray(this.items, item);

    this.visionboardDragDropService.onDrag(item, DragDropPanelType.WorkspaceVenueItems);
  }

  onDragReleased(event: CdkDragRelease, item: VisionboardItem): void {
    this.visionboardDragDropService.onDrop();
  }

  onDragMoved(event: CdkDragMove, item: VisionboardItem): void {
    const rect = event.source.element.nativeElement.getBoundingClientRect();

    const itemRect = new Rect(rect.left, rect.top, rect.width, rect.height);
    this.visionboardDragDropService.onMove(itemRect);
  }

  onItemClick(): void {
    this.snackBarService.show({
      action: "Hide",
      message: "Venue provided items cannot be removed or changed",
      duration: 5000,
    });
  }

  trackWorkspaceItemsFn(index: number, item: VisionboardItem): any {
    return item.internalId;
  }

  onAfterExpand(): void {
    this._storageService.local.set(LocalStorageKeys.VisionboardFreeformVenueItemsCollapsed, "0");
    this.panelOpenState = true;
  }

  onAfterCollapse(): void {
    this.panelOpenState = false;
    this._storageService.local.set(LocalStorageKeys.VisionboardFreeformVenueItemsCollapsed, "1");
  }

  private setHeightAndRecalcItemLocations(sizeFactor: number): void {
    this.visionboardWorkspaceFreeformService.recalcItemLocations(
      this.items,
      this.visionboardDragDropService.getWorkspaceVenueItemsRect(),
      true,
      sizeFactor,
      true,
    );
    this.setMessageWidth();
  }

  private setMessageWidth(): void {
    const message = document.getElementById("visionboard-workspace-venue-items-message");
    const items = document.getElementById("visionboard-workspace-venue-items");

    if (message && items) {
      message.style.width = `${items.getBoundingClientRect().width - 10}px`;
    }
  }

  private _resetItems(): void {
    this.items = this._fullItemSet?.slice();
  }
}
